import React from 'react';
import { Loader2, RefreshCw } from 'lucide-react';
import { cn } from '../lib/utils';

/**
 * Loading Spinner Component
 * Displays a loading indicator with customizable size and appearance
 */
export const LoadingSpinner = ({ 
  size = 'default', 
  className = '',
  text = '',
  variant = 'default'
}) => {
  const sizeClasses = {
    sm: 'h-4 w-4',
    default: 'h-6 w-6',
    lg: 'h-8 w-8',
    xl: 'h-12 w-12'
  };

  const variantClasses = {
    default: 'text-blue-600',
    white: 'text-white',
    gray: 'text-gray-600',
    primary: 'text-primary'
  };

  return (
    <div className={cn('flex items-center justify-center', className)}>
      <div className="flex flex-col items-center gap-2">
        <Loader2 
          className={cn(
            'animate-spin',
            sizeClasses[size],
            variantClasses[variant]
          )} 
        />
        {text && (
          <p className={cn(
            'text-sm',
            variant === 'white' ? 'text-white' : 'text-gray-600'
          )}>
            {text}
          </p>
        )}
      </div>
    </div>
  );
};

/**
 * Loading Overlay Component
 * Displays a full-screen or container overlay with loading indicator
 */
export const LoadingOverlay = ({ 
  isLoading, 
  children, 
  text = 'Загрузка...',
  className = '',
  overlay = true
}) => {
  if (!isLoading) return children;

  return (
    <div className={cn('relative', className)}>
      {children}
      {overlay && (
        <div className="absolute inset-0 bg-white/80 backdrop-blur-sm flex items-center justify-center z-50">
          <div className="bg-white rounded-lg shadow-lg p-6 flex flex-col items-center gap-3">
            <LoadingSpinner size="lg" />
            <p className="text-gray-600">{text}</p>
          </div>
        </div>
      )}
    </div>
  );
};

/**
 * Loading Button Component
 * Button that shows loading state
 */
export const LoadingButton = ({ 
  isLoading, 
  loadingText = 'Загрузка...',
  children, 
  className = '',
  ...props 
}) => {
  return (
    <button
      className={cn(
        'flex items-center justify-center gap-2',
        'disabled:opacity-50 disabled:cursor-not-allowed',
        className
      )}
      disabled={isLoading}
      {...props}
    >
      {isLoading ? (
        <>
          <Loader2 className="h-4 w-4 animate-spin" />
          {loadingText}
        </>
      ) : (
        children
      )}
    </button>
  );
};

/**
 * Loading Card Component
 * Card with loading skeleton
 */
export const LoadingCard = ({ 
  lines = 3, 
  className = '',
  showAvatar = false 
}) => {
  return (
    <div className={cn('animate-pulse', className)}>
      <div className="bg-white rounded-lg shadow p-6">
        {showAvatar && (
          <div className="flex items-center gap-3 mb-4">
            <div className="h-10 w-10 bg-gray-200 rounded-full"></div>
            <div className="flex-1">
              <div className="h-4 bg-gray-200 rounded w-1/3 mb-2"></div>
              <div className="h-3 bg-gray-200 rounded w-1/4"></div>
            </div>
          </div>
        )}
        
        <div className="space-y-3">
          {Array.from({ length: lines }).map((_, i) => (
            <div
              key={i}
              className={cn(
                'h-4 bg-gray-200 rounded',
                i === lines - 1 ? 'w-2/3' : 'w-full'
              )}
            ></div>
          ))}
        </div>
      </div>
    </div>
  );
};

/**
 * Loading Table Component
 * Table with loading skeleton
 */
export const LoadingTable = ({ 
  rows = 5, 
  columns = 4,
  className = '' 
}) => {
  return (
    <div className={cn('animate-pulse', className)}>
      <div className="bg-white rounded-lg shadow overflow-hidden">
        {/* Header */}
        <div className="bg-gray-50 px-6 py-3 border-b">
          <div className="grid gap-4" style={{ gridTemplateColumns: `repeat(${columns}, 1fr)` }}>
            {Array.from({ length: columns }).map((_, i) => (
              <div key={i} className="h-4 bg-gray-200 rounded"></div>
            ))}
          </div>
        </div>
        
        {/* Rows */}
        {Array.from({ length: rows }).map((_, rowIndex) => (
          <div key={rowIndex} className="px-6 py-4 border-b last:border-b-0">
            <div className="grid gap-4" style={{ gridTemplateColumns: `repeat(${columns}, 1fr)` }}>
              {Array.from({ length: columns }).map((_, colIndex) => (
                <div 
                  key={colIndex} 
                  className={cn(
                    'h-4 bg-gray-200 rounded',
                    colIndex === 0 ? 'w-3/4' : 'w-full'
                  )}
                ></div>
              ))}
            </div>
          </div>
        ))}
      </div>
    </div>
  );
};

/**
 * Loading List Component
 * List with loading skeleton
 */
export const LoadingList = ({ 
  items = 5, 
  className = '',
  showAvatar = false 
}) => {
  return (
    <div className={cn('animate-pulse space-y-3', className)}>
      {Array.from({ length: items }).map((_, i) => (
        <div key={i} className="bg-white rounded-lg shadow p-4">
          <div className="flex items-center gap-3">
            {showAvatar && (
              <div className="h-10 w-10 bg-gray-200 rounded-full flex-shrink-0"></div>
            )}
            <div className="flex-1 space-y-2">
              <div className="h-4 bg-gray-200 rounded w-3/4"></div>
              <div className="h-3 bg-gray-200 rounded w-1/2"></div>
            </div>
          </div>
        </div>
      ))}
    </div>
  );
};

/**
 * Loading Hook
 * Hook for managing loading states
 */
export const useLoading = (initialState = false) => {
  const [isLoading, setIsLoading] = React.useState(initialState);
  const [loadingText, setLoadingText] = React.useState('');

  const startLoading = React.useCallback((text = 'Загрузка...') => {
    setLoadingText(text);
    setIsLoading(true);
  }, []);

  const stopLoading = React.useCallback(() => {
    setIsLoading(false);
    setLoadingText('');
  }, []);

  const withLoading = React.useCallback(async (asyncFn, loadingText = 'Загрузка...') => {
    try {
      startLoading(loadingText);
      const result = await asyncFn();
      return result;
    } finally {
      stopLoading();
    }
  }, [startLoading, stopLoading]);

  return {
    isLoading,
    loadingText,
    startLoading,
    stopLoading,
    withLoading
  };
};

/**
 * Loading Context
 * Context for global loading state management
 */
const LoadingContext = React.createContext();

export const LoadingProvider = ({ children }) => {
  const [globalLoading, setGlobalLoading] = React.useState(false);
  const [loadingMessage, setLoadingMessage] = React.useState('');

  const startGlobalLoading = React.useCallback((message = 'Загрузка...') => {
    setLoadingMessage(message);
    setGlobalLoading(true);
  }, []);

  const stopGlobalLoading = React.useCallback(() => {
    setGlobalLoading(false);
    setLoadingMessage('');
  }, []);

  const value = {
    globalLoading,
    loadingMessage,
    startGlobalLoading,
    stopGlobalLoading
  };

  return (
    <LoadingContext.Provider value={value}>
      {children}
      {globalLoading && (
        <div className="fixed inset-0 bg-black/50 backdrop-blur-sm flex items-center justify-center z-50">
          <div className="bg-white rounded-lg shadow-xl p-8 flex flex-col items-center gap-4">
            <LoadingSpinner size="xl" />
            <p className="text-lg font-medium text-gray-900">{loadingMessage}</p>
          </div>
        </div>
      )}
    </LoadingContext.Provider>
  );
};

export const useGlobalLoading = () => {
  const context = React.useContext(LoadingContext);
  if (!context) {
    throw new Error('useGlobalLoading must be used within a LoadingProvider');
  }
  return context;
};

export default LoadingSpinner;
